(function () {
  'use strict';

  const script = document.currentScript;
  const origins = new Set(script.dataset.storefrontOrigins.split(','));
  const prefix = '#bark-cart:';
  const syncKey = 'bark:cart:sync';
  const pendingKey = 'bark:cart:pending';
  const lifetime = 5 * 60 * 1000;
  const maxBytes = 48000;
  const copy = {
    da: { loading: 'Opdaterer din kurv…', error: 'Vi kunne ikke overføre hele din kurv. Dine varer er stadig gemt på den tidligere side.', retry: 'Prøv igen', back: 'Tilbage', switchError: 'Kurven kunne ikke overføres lige nu. Prøv igen.' },
    sv: { loading: 'Uppdaterar din varukorg…', error: 'Vi kunde inte överföra hela din varukorg. Dina varor finns kvar på den tidigare sidan.', retry: 'Försök igen', back: 'Tillbaka', switchError: 'Varukorgen kunde inte överföras just nu. Försök igen.' },
    en: { loading: 'Updating your cart…', error: 'We could not transfer your entire cart. Your items are still saved on the previous site.', retry: 'Try again', back: 'Go back', switchError: 'Your cart could not be transferred right now. Please try again.' }
  };

  function text() { return copy[document.documentElement.lang.split('-')[0]] || copy.en; }
  function read(key, storage) {
    const value = storage.getItem(key);
    if (!value) return null;
    try { return JSON.parse(value); }
    catch (error) { storage.removeItem(key); return null; }
  }
  function write(key, value, storage) { storage.setItem(key, JSON.stringify(value)); }
  function pairs(value) { return Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)); }
  function identity(item) { return JSON.stringify([item.id, item.selling_plan || null, pairs(item.properties)]); }
  function quantities(items) {
    const result = new Map();
    items.forEach(item => {
      const key = identity(item);
      const existing = result.get(key);
      result.set(key, { ...item, quantity: item.quantity + (existing ? existing.quantity : 0) });
    });
    return result;
  }
  function automaticGift(item) { return item.url?.includes('sca_clone_freegift'); }
  function snapshot(cart) {
    return {
      items: cart.items.filter(item => !automaticGift(item)).map(item => ({ id: item.variant_id, quantity: item.quantity, properties: item.properties || {},
        ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) })),
      note: cart.note || '', attributes: cart.attributes || {},
      discounts: [...new Set([...(cart.cart_level_discount_applications || []), ...cart.items.flatMap(item => (item.line_level_discount_allocations || []).map(entry => entry.discount_application))].filter(discount => discount.type === 'discount_code').map(discount => discount.title))]
    };
  }

  // A shared baseline carries removals across domains. Concurrent edits keep the larger quantity.
  function reconcile(incoming, current, baseline) {
    const remote = quantities(incoming.items);
    const local = quantities(current.items);
    const previous = quantities(baseline ? baseline.items : []);
    const items = [];
    for (const key of new Set([...remote.keys(), ...local.keys(), ...previous.keys()])) {
      const a = remote.get(key)?.quantity || 0;
      const b = local.get(key)?.quantity || 0;
      const old = previous.get(key)?.quantity || 0;
      const quantity = baseline ? (a === old ? b : b === old ? a : Math.max(a, b)) : Math.max(a, b);
      if (quantity) items.push({ ...(remote.get(key) || local.get(key)), quantity });
    }
    const attributes = { ...current.attributes };
    for (const key of new Set([...Object.keys(incoming.attributes), ...Object.keys(current.attributes), ...Object.keys(baseline?.attributes || {})])) {
      if (baseline ? current.attributes[key] === baseline.attributes[key] : current.attributes[key] === undefined) attributes[key] = incoming.attributes[key] ?? '';
    }
    const note = !baseline ? (current.note || incoming.note) : current.note === baseline.note ? incoming.note : current.note;
    const discounts = baseline && JSON.stringify(current.discounts) === JSON.stringify(baseline.discounts)
      ? incoming.discounts : [...new Set([...current.discounts, ...incoming.discounts])];
    return { items, note, attributes, discounts };
  }

  function validSnapshot(value) {
    const record = object => object && typeof object === 'object' && !Array.isArray(object)
      && Object.entries(object).every(([key, entry]) => key.length <= 255 && ['string', 'number', 'boolean'].includes(typeof entry));
    return value && Array.isArray(value.items) && value.items.length <= 200 && value.items.every(item =>
      Number.isSafeInteger(item.id) && item.id > 0 && Number.isSafeInteger(item.quantity) && item.quantity > 0
      && item.quantity <= 1000000 && record(item.properties)
      && (item.selling_plan === undefined || Number.isSafeInteger(item.selling_plan) && item.selling_plan > 0))
      && typeof value.note === 'string' && record(value.attributes) && Array.isArray(value.discounts)
      && value.discounts.every(code => typeof code === 'string');
  }
  function validTransfer(value) {
    if (!value || value.version !== 1 || typeof value.id !== 'string' || value.id.length > 80
      || !Number.isFinite(value.expiresAt) || value.expiresAt <= Date.now() || value.expiresAt > Date.now() + lifetime
      || typeof value.hash !== 'string' || !validSnapshot(value.cart)) return false;
    try {
      const source = new URL(value.source);
      return origins.has(source.origin) && origins.has(location.origin) && source.origin !== location.origin
        && !source.username && !source.password && value.destination === location.origin;
    } catch (error) { return false; }
  }
  function encode(value) {
    const bytes = new TextEncoder().encode(JSON.stringify(value));
    if (bytes.length > maxBytes) throw new Error('Cart transfer exceeds the supported payload size.');
    return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  async function api(path, body) {
    const root = window.Shopify.routes.root.replace(/\/$/, '');
    const response = await fetch(`${root}/cart${path}.js`, {
      credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(15000),
      ...(body === undefined ? {} : { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || `Cart request failed: ${response.status}`);
    return result;
  }

  async function navigate(destination) {
    if (!origins.has(destination.origin) || destination.origin === location.origin) throw new Error('Invalid cart destination.');
    const cart = await api('');
    const existing = read(syncKey, localStorage);
    const id = existing?.token === cart.token && typeof existing.id === 'string' && validSnapshot(existing.cart) ? existing.id : crypto.randomUUID();
    if (cart.items.some(item => item.parent_relationship)) throw new Error('Nested cart lines require a supported parent transfer.');
    const current = snapshot(cart);
    if (!validSnapshot(current)) throw new Error('The cart cannot be transferred safely.');
    write(syncKey, { id, token: cart.token, cart: current }, localStorage);
    const transfer = { version: 1, id, cart: current, source: location.href, destination: destination.origin,
      hash: destination.hash, expiresAt: Date.now() + lifetime };
    destination.hash = prefix + encode(transfer);
    location.assign(destination.href);
  }

  let pending = null;
  let arrivalError = null;
  if (window.BarkCartArrival) {
    try {
      const transfer = window.BarkCartArrival.payload;
      if (!validTransfer(transfer)) throw new Error('Invalid or expired cart transfer.');
      pending = { transfer };
      write(pendingKey, pending, sessionStorage);
    } catch (error) { arrivalError = error; }
    delete window.BarkCartArrival;
  } else {
    try {
      const saved = read(pendingKey, sessionStorage);
      if (saved && validTransfer(saved.transfer)) pending = saved;
      else if (saved) {
        arrivalError = new Error('The cart transfer has expired.');
        const source = new URL(saved.transfer.source);
        if (origins.has(source.origin) && source.origin !== location.origin && !source.username && !source.password) pending = saved;
      }
    } catch (error) { /* Ordinary browsing must work when storage is unavailable. */ }
  }

  window.BarkCartTransfer = { navigate, switchError: () => text().switchError, reconcile };
  if (!pending && !arrivalError) return;
  document.documentElement.classList.add('bark-cart-restoring');

  async function apply(desired) {
    let current = await api('');
    const wanted = quantities(desired.items);
    const existing = quantities(snapshot(current).items);
    const additions = [...wanted].map(([key, item]) => ({ ...item, quantity: item.quantity - (existing.get(key)?.quantity || 0) }))
      .filter(item => item.quantity > 0);
    // Add and validate first. An unavailable item must not erase the destination's original lines.
    if (additions.length) await api('/add', { items: additions });
    current = await api('');
    const remaining = new Map([...wanted].map(([key, item]) => [key, item.quantity]));
    const updates = {};
    for (const item of current.items) {
      // FreeGifts owns cloned gift lines and recalculates eligibility in the destination market.
      if (automaticGift(item)) continue;
      const key = identity(snapshot({ items: [item] }).items[0]);
      const quantity = Math.min(item.quantity, remaining.get(key) || 0);
      remaining.set(key, (remaining.get(key) || 0) - quantity);
      if (quantity !== item.quantity) updates[item.key] = quantity;
    }
    if ([...remaining.values()].some(quantity => quantity > 0)) throw new Error('The destination could not supply the complete cart.');
    await api('/update', { updates, note: desired.note, attributes: desired.attributes, discount: desired.discounts.join(',') });
    const after = await api('');
    const actual = quantities(snapshot(after).items);
    if (actual.size !== wanted.size || [...wanted].some(([key, item]) => actual.get(key)?.quantity !== item.quantity)) {
      throw new Error('The transferred cart does not match the requested quantities.');
    }
    return after;
  }

  function start() {
    const overlay = document.createElement('div');
    overlay.className = 'BarkCartTransfer';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', text().loading);
    const message = document.createElement('p');
    message.setAttribute('role', 'status');
    const retry = document.createElement('button');
    retry.type = 'button'; retry.textContent = text().retry;
    const back = document.createElement('button');
    back.type = 'button'; back.textContent = text().back;
    message.tabIndex = -1;
    overlay.append(message, retry, back);
    document.body.append(overlay);
    back.addEventListener('click', () => {
      sessionStorage.removeItem(pendingKey);
      if (pending) location.assign(pending.transfer.source);
      else { document.documentElement.classList.remove('bark-cart-restoring'); overlay.remove(); }
    });
    async function restore() {
      retry.hidden = true; back.hidden = true;
      message.textContent = text().loading;
      message.focus();
      try {
        if (arrivalError) throw arrivalError;
        const before = await api('');
        if (pending.desired && !validSnapshot(pending.desired)) throw new Error('Invalid saved cart plan.');
        if (!pending.desired) {
          const record = read(syncKey, localStorage);
          const baseline = record?.id === pending.transfer.id && record.token === before.token && validSnapshot(record.cart) ? record.cart : null;
          pending.desired = reconcile(pending.transfer.cart, snapshot(before), baseline);
          // Keep one plan across partial failures, retries and reloads; never add the snapshot twice.
          write(pendingKey, pending, sessionStorage);
        }
        const after = await apply(pending.desired);
        write(syncKey, { id: pending.transfer.id, token: after.token, cart: snapshot(after) }, localStorage);
        sessionStorage.removeItem(pendingKey);
        location.reload();
      } catch (error) {
        message.textContent = text().error;
        retry.hidden = Boolean(arrivalError); back.hidden = false;
        (arrivalError ? back : retry).focus();
      }
    }
    retry.addEventListener('click', restore);
    restore();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
